import { Button, IconButton, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Resource } from 'sr-types/lib/production/v1/graphql';
import { EngagementTrackerProvider } from '../common/components/person/engagementTracker/EngagementContext';
import { StepsIndexConstant } from '../common/components/Stepper';
import { useStepper } from '../common/components/StepperContext';
import { CONSTANTS_ID } from '../common/constants/constants';
import { FormContext } from '../common/form/FormContext';
import { useIsWithinBreakpoints } from '../common/hooks/useIsWithinBreakpoints';
import I18n, { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { H, V } from '../common/Layout';
import { MarketplaceProfileDetails } from '../marketplace/MarketplaceProfileDetails';
import OrganizationMarketplaceList from '../marketplace/OrganizationMarketplaceList';
import QuickAddOrganizationFormModal from '../organization/QuickAddOrganizationFormModal';
import { toInputForSaveResource } from '../resource/resourceUtils';
import { OrganizationInviteFormProps } from '../shortlists/form/inquiry/inquiry';
import OrganizationInviteFormItems from '../shortlists/form/OrganizationInviteFormItems';
import { buildOrgPrefilters } from '../shortlists/shortlistLineItem';
import { formatItemForOrg, handleItemSelection } from './hireResourceUtils';

export default ({
    expandMarketplace,
    expandedContainer,
    data,
    isRequestAvailsForUser = false,
    filters,
    saveAttachmentTags,
    onDeleteAttachmentFromState,
    setIsInUploadMode,
    isAttachmentsInUploadMode
}: OrganizationInviteFormProps) => {
    const t = useI18n();
    const { state, validation, setState, handleChange } = useContext(FormContext);
    const { getStepData, setStepCompletion, setStepData, activeStep } = useStepper();
    const [selectedItem, setSelectedItem] = useState();
    const [detailsVisible, setDetailsVisible] = useState(false);
    const shortlistLineItems = useRef<string[]>([]);
    const isSmallScreen = useIsWithinBreakpoints(['xs']);
    const [isMarketplaceOpen, setIsMarketplaceOpen] = useState<boolean>(expandedContainer);
    const currentOrgData = getStepData(activeStep);
    const resourceDataFormValue = getStepData(StepsIndexConstant.one);
    const resourceData =
        resourceDataFormValue &&
        toInputForSaveResource(resourceDataFormValue?.identity?.id || CONSTANTS_ID.createId, resourceDataFormValue);
    const [addOrgModalState, setAddOrgModalState] = useState(false);
    const [addOrgPublicName, setAddOrgPublicName] = useState<string>(undefined);

    useEffect(() => {
        if (!isEqual(currentOrgData, state)) {
            setStepData(activeStep, state);
        }
    }, [currentOrgData, state]);

    useEffect(() => {
        if (!isEqual(currentOrgData?.organizations, state?.organizations)) {
            setState({
                ...state,
                organizations: state.organizations,
                notifyContacts: state.organizations.length > 1 ? [] : state.notifyContacts
            });
        }
    }, [state?.organizations, state?.organizations?.length]);

    useEffect(() => {
        setStepCompletion(activeStep, validation?.isValid && !isAttachmentsInUploadMode);
    }, [validation?.isValid, isAttachmentsInUploadMode]);

    useEffect(() => {
        shortlistLineItems.current = state?.organizations?.map((org) => org.id || org.ref.id);
    }, [state?.organizations]);

    const orgPreFilters = useMemo(
        () =>
            buildOrgPrefilters({
                category: resourceData?.resourceCategory,
                locations: resourceData?.locations
            }),
        [resourceData]
    );

    const addFromShortlist = (item) => {
        const formattedItem = formatItemForOrg(item);
        setState((prev) => updateShortlistState(prev, formattedItem, item.id));
    };

    const updateShortlistState = (prev, formattedItem, itemId) => {
        const isItemInShortlist = prev?.organizations?.some((p) => p.id === itemId);
        if (!isItemInShortlist) {
            const updatedOrg = [...(Array.isArray(prev.organizations) ? prev.organizations : []), formattedItem];
            handleChange('organizations', { ...prev, organizations: updatedOrg });
            return { ...prev, organizations: updatedOrg };
        }
        return prev;
    };

    const handleOnCloseAddOrgModal = () => {
        setAddOrgPublicName(undefined);
        setState((prev) => ({
            ...prev,
            organizations: prev?.organizations?.filter((org) => !org.createOption) ?? []
        }));
    };

    const isTwoColumnLayout = isMarketplaceOpen && !isSmallScreen;
    const formColumnWidth = isTwoColumnLayout ? '60%' : '100%';
    const marketplaceColumnWidth = isTwoColumnLayout ? '40%' : '100%';
    const formColumnFlex = isTwoColumnLayout ? '0 0 60%' : '1 1 100%';
    const marketplaceColumnFlex = isTwoColumnLayout ? '0 0 40%' : '1 1 100%';

    return (
        <H sx={{ flexGrow: 1, overflow: 'hidden', alignItems: 'stretch' }}>
            <V
                fill
                sx={{ flex: formColumnFlex, maxWidth: formColumnWidth, minWidth: 0, pr: isTwoColumnLayout ? 2 : 0 }}
            >
                <Button
                    onClick={() => {
                        expandMarketplace();
                        setIsMarketplaceOpen((prev) => !prev);
                    }}
                    sx={{ display: 'flex', gap: 1, mt: 1, ml: 2 }}
                >
                    <Icons.Search />
                    {t('marketplace.title')}
                </Button>

                {!isSmallScreen || !isMarketplaceOpen ? (
                    <OrganizationInviteFormItems
                        data={data}
                        isRequestAvailsForUser={isRequestAvailsForUser}
                        filters={filters}
                        setAddOrgModalState={setAddOrgModalState}
                        setAddOrgPublicName={setAddOrgPublicName}
                        saveAttachmentTags={saveAttachmentTags}
                        onDeleteAttachmentFromState={onDeleteAttachmentFromState}
                        setIsInUploadMode={setIsInUploadMode}
                    />
                ) : (
                    <></>
                )}
            </V>
            {isMarketplaceOpen && (
                <V
                    fill
                    sx={{
                        flex: marketplaceColumnFlex,
                        maxWidth: marketplaceColumnWidth,
                        minWidth: 0,
                        pr: isTwoColumnLayout ? 2 : 0
                    }}
                >
                    <H sx={{ justifyContent: 'space-between', px: 1 }}>
                        <Typography fontSize={16}>
                            {t('marketplace.title')}
                        </Typography>
                        <H sx={{ width: 'unset' }}>
                            {detailsVisible && (
                                <IconButton onClick={() => setDetailsVisible(false)}>
                                    <Icons.BackButton />
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() => {
                                    expandMarketplace();
                                    setIsMarketplaceOpen(false);
                                }}
                            >
                                <Icons.Close />
                            </IconButton>
                        </H>
                    </H>
                    <V fill sx={{ overflow: 'auto' }}>
                        {detailsVisible ? (
                            <EngagementTrackerProvider>
                                <MarketplaceProfileDetails
                                    item={selectedItem}
                                    shortlistLineItems={shortlistLineItems}
                                    onAddToShortlist={addFromShortlist}
                                    isOrganizationDetails
                                    isProfilePreview
                                />
                            </EngagementTrackerProvider>
                        ) : (
                            <OrganizationMarketplaceList
                                id="org-hire-crew-marketplace"
                                onAddToShortlist={(item) => addFromShortlist(item)}
                                shortlistLineItems={shortlistLineItems}
                                immutableFilters={orgPreFilters}
                                handleItemClick={(item) =>
                                    handleItemSelection(item, setSelectedItem, setDetailsVisible)
                                }
                                resourceData={resourceData}
                                hasFacets={false}
                                showBreadCrumbs={false}
                            />
                        )}
                    </V>
                </V>
            )}
            {addOrgModalState && (
                <QuickAddOrganizationFormModal
                    id={'quick-add-from-stepper'}
                    setOrganizationModalState={setAddOrgModalState}
                    organizationModalState={addOrgModalState}
                    resourceData={resourceData as Resource}
                    onAddToShortlist={addFromShortlist}
                    addOrgPublicName={addOrgPublicName}
                    handleOnCloseAddOrgModal={handleOnCloseAddOrgModal}
                />
            )}
        </H>
    );
};
